import { useState, useEffect } from "react";
import styles from "./AllHomeSections.module.css";
import { FiSearch } from "react-icons/fi";
import { ENDPOINTS } from "../../utils/endpoints";
import api from "../../services/api";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";
import useDashboardStore from "../../store/dashboardStore";
import dayjs from "dayjs";

const AllHomeSections = () => {
  const { setIsCreateHomeSectionScreen, setEditHomeSectionId } =
    useDashboardStore();
  const [homeSections, setHomeSections] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const handleEdit = (section) => {
    setEditHomeSectionId(section.id);
    setIsCreateHomeSectionScreen();
  };

  const handleDelete = (section) => {
    confirmAlert({
      title: "Confirm Delete",
      message: "Are you sure you want to delete this home section?",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            try {
              const { data } = await api.delete(
                ENDPOINTS.DELETE_HOME_SECTION_BY_ID(section.id)
              );
              if (data.success) {
                fetchHomeSections();
                toast.success(
                  data.message || "Home section deleted successfully"
                );
              }
            } catch (err) {
              console.error("Failed to delete home section", err);
              toast.error("Failed to delete home section");
            }
          },
        },
        {
          label: "No",
          onClick: () => toast.error("Home section deletion cancelled"),
        },
      ],
    });
  };

  const moveSection = async (section, direction) => {
    const currentIndex = homeSections.indexOf(section);

    if (direction === "up" && currentIndex > 0) {
      const previousSection = homeSections[currentIndex - 1];
      await swapSortOrders(section, previousSection);
    } else if (direction === "down" && currentIndex < homeSections.length - 1) {
      const nextSection = homeSections[currentIndex + 1];
      await swapSortOrders(section, nextSection);
    }
  };

  const swapSortOrders = async (section1, section2) => {
    try {
      const order1 = section1.sort_order;
      const order2 = section2.sort_order;

      const { data } = await api.put(ENDPOINTS.REORDER_HOME_SECTIONS, {
        orders: [
          { id: section1.id, sort_order: order2 },
          { id: section2.id, sort_order: order1 },
        ],
      });

      if (data.success) {
        fetchHomeSections();
        toast.success("Home sections reordered successfully");
      }
    } catch (err) {
      console.error("Failed to reorder sections", err);
      toast.error("Failed to reorder sections");
    }
  };

  const fetchHomeSections = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get(ENDPOINTS.GET_ALL_HOME_SECTIONS);
      if (data.success) {
        setHomeSections(data.sections);
      }
    } catch (err) {
      console.error("Failed to fetch home sections", err);
      toast.error("Failed to fetch home sections");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHomeSections();
  }, []);

  const filteredSections = homeSections.filter((section) =>
    (section.title || section.body_text || "")
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.appBar}>
          <h2 className={styles.title}>Home Sections</h2>
          <div className={styles.searchContainer}>
            <input
              type="text"
              placeholder="Type in to search .."
              className={styles.searchInput}
              disabled
            />
            <FiSearch className={styles.searchIcon} />
          </div>
        </div>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Loading home sections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.appBar}>
        <h2 className={styles.title}>Home Sections</h2>
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Type in to search .."
            className={styles.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <FiSearch className={styles.searchIcon} />
        </div>
      </div>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Order</th>
              <th></th>
              <th>Title</th>
              <th>Has Image</th>
              <th>Has Body</th>
              <th>Has Button</th>
              <th>Status</th>
              <th>Created At</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredSections.map((section, index) => (
              <tr key={section.id}>
                <td className={styles.orderCell}>{section.sort_order}</td>
                <td>
                  <div className={styles.reorderButtons}>
                    <button
                      className={styles.arrowButton}
                      onClick={() => moveSection(section, "up")}
                      disabled={index === 0}
                    >
                      ▲
                    </button>
                    <button
                      className={styles.arrowButton}
                      onClick={() => moveSection(section, "down")}
                      disabled={index === filteredSections.length - 1}
                    >
                      ▼
                    </button>
                  </div>
                </td>
                <td className={styles.titleCell}>
                  {section.title || "(No Title)"}
                </td>
                <td className={styles.iconCell}>
                  {section.image_url ? (
                    <span className={styles.checkIcon}>✓</span>
                  ) : (
                    <span className={styles.crossIcon}>✕</span>
                  )}
                </td>
                <td className={styles.iconCell}>
                  {section.body_text ? (
                    <span className={styles.checkIcon}>✓</span>
                  ) : (
                    <span className={styles.crossIcon}>✕</span>
                  )}
                </td>
                <td className={styles.iconCell}>
                  {section.button_text ? (
                    <span className={styles.checkIcon}>✓</span>
                  ) : (
                    <span className={styles.crossIcon}>✕</span>
                  )}
                </td>
                <td>
                  <span
                    className={
                      section.is_active
                        ? styles.activeStatus
                        : styles.inactiveStatus
                    }
                  >
                    {section.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>{dayjs(section.created_at).format("DD-MM-YYYY")}</td>
                <td>
                  <span
                    className={styles.edit}
                    onClick={() => handleEdit(section)}
                  >
                    Edit
                  </span>
                  <span className={styles.divider}> - </span>
                  <span
                    className={styles.delete}
                    onClick={() => handleDelete(section)}
                  >
                    Delete
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AllHomeSections;
